import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import { axiosInstance as axios, setSession } from '../utils/axios';
import { setChatSession } from '../utils/chat';
// eslint-disable-next-line import/no-cycle
import { store } from '../redux/store';
import { endChatSession } from '../redux/slices/chat';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  invitation: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    if (user.business) localStorage.setItem('business', user.business.id);
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    if (user.business) localStorage.setItem('business', user.business.id);
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SETBUSINESS: (state, action) => {
    const { business } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user: {
        ...state.user,
        business,
      },
    };
  },
  INVITATION: (state, action) => ({
    ...state,
    invitation: action.payload,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  hasPermissions: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  getSSOUrl: () => Promise.resolve(),
  loginSSO: () => Promise.resolve(),
  register: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  verifyResetPasswordCode: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
  verifyActivationCode: () => Promise.resolve(),
  sendActivationEmail: () => Promise.resolve(),
  checkOTP: () => Promise.resolve(),
  createDemo: () => Promise.resolve(),
  checkDemo: () => Promise.resolve(),
  deleteData: () => Promise.resolve(),
  setBusiness: () => Promise.resolve(),
  checkDiscount: () => Promise.resolve(),
  placeSubscription: () => Promise.resolve(),
  verifySubscription: () => Promise.resolve(),
  updateBusiness: () => Promise.resolve(),
  partialUpdateBusiness: () => Promise.resolve(),
  setSystemParameter: () => Promise.resolve(),
  sendInvitationEmail: () => Promise.resolve(),
  getInvitation: () => Promise.resolve(),
  acceptInvitation: () => Promise.resolve(),
  updateProfile: () => Promise.resolve(),
  setUserCounts: () => Promise.resolve(),
  egsOnboarding: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const token = window.localStorage.getItem('token');
        const business = window.localStorage.getItem('business');

        if (token) {
          setSession(token, business);

          const response = await axios.get('/auth/user');
          const user = response.data;

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          setChatSession(user);
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const hasPermissions = (permissions, every = false) => {
    if (state.user) {
      if (permissions.length === 0) return true;
      if (state.user.business) {
        if (every) return permissions.every((permission) => state.user.business.permissions.includes(permission));
        return permissions.some((permission) => state.user.business.permissions.includes(permission));
      }
    }
    return false;
  };

  const login = async (email, password) => {
    try {
      setSession(null, null);
      const response = await axios.post('/auth/login', {
        email,
        password,
      });
      const { token, user } = response.data;
      setSession(token, user?.business?.id);
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
      setChatSession(user);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const logout = async () => {
    setSession(null, null);
    store.dispatch(endChatSession());
    dispatch({ type: 'LOGOUT' });
  };

  const getSSOUrl = async (platform) => {
    try {
      const response = await axios.get('/auth/sso', {
        params: {
          platform,
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const loginSSO = async (data) => {
    try {
      const response = await axios.post('/auth/sso', data);
      const { token, user } = response.data;
      setSession(token, user?.business?.id);
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
      setChatSession(user);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const register = async (data) => {
    try {
      setSession(null, null);
      const response = await axios.post('/auth/register', data);
      const { token, user } = response.data;
      setSession(token, user?.business?.id);
      dispatch({
        type: 'REGISTER',
        payload: {
          user,
        },
      });
      setChatSession(user);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post('/auth/forgot_password', { email });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const verifyResetPasswordCode = async (code) => {
    try {
      const response = await axios.post('/auth/reset_password', { code });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const resetPassword = async (password, code) => {
    try {
      const response = await axios.put('/auth/reset_password', { password, code });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const changePassword = async (data) => {
    try {
      await axios.post('/auth/change_password', data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const verifyActivationCode = async (code) => {
    try {
      const response = await axios.post('/auth/verify_activation_code', { code });
      const { token, user } = response.data;
      setSession(token, user?.business?.id);
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
      setChatSession(user);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const sendActivationEmail = async () => {
    try {
      await axios.post('/auth/send_activation_email', null);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const checkOTP = async (data) => {
    try {
      const response = await axios.post('/auth/check_otp', data);
      const { token, user } = response.data;
      setSession(token, user?.business?.id);
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
      setChatSession(user);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const createDemo = async (data) => {
    try {
      const response = await axios.post('/auth/demo', data);
      const { token, user } = response.data;
      if (user) {
        setSession(token, user?.business?.id);
        dispatch({
          type: 'LOGIN',
          payload: {
            user,
          },
        });
        setChatSession(user);
      }
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const checkDemo = async () => {
    try {
      const response = await axios.get('/auth/demo');
      const { token, user } = response.data;
      if (user) {
        setSession(token, user?.business?.id);
        dispatch({
          type: 'LOGIN',
          payload: {
            user,
          },
        });
        setChatSession(user);
      }
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const deleteData = async () => {
    try {
      const response = await axios.delete('/auth/demo');
      const { token, user } = response.data;
      setSession(token, user?.business?.id);
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
      setChatSession(user);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const setBusiness = async (business) => {
    localStorage.setItem('business', business.id);
    const token = window.localStorage.getItem('token');
    setSession(token, business.id);
    dispatch({
      type: 'SETBUSINESS',
      payload: {
        business,
      },
    });
    setChatSession({ ...state.user, business });
  };

  const checkDiscount = async (data) => {
    try {
      const response = await axios.post('/business/check_discount', data);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const placeSubscription = async (data) => {
    try {
      const response = await axios.post('/business/place_subscription', data);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const verifySubscription = async (code) => {
    try {
      const response = await axios.post('/business/verify_subscription', { code });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const updateBusiness = async (data) => {
    try {
      const response = await axios.put('/business/update_business', data);
      const { user } = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const partialUpdateBusiness = async (data) => {
    try {
      const response = await axios.patch('/business/update_business', data);
      const { user } = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const setSystemParameter = async (data) => {
    try {
      const response = await axios.post('/system/set_parameter', data);
      const { user } = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const sendInvitationEmail = async (id) => {
    try {
      await axios.post('/auth/send_invitation_email', { id });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const getInvitation = async (code) => {
    try {
      const response = await axios.post('/auth/get_invitation', { code });
      dispatch({
        type: 'INVITATION',
        payload: response.data,
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const acceptInvitation = async (name, password, mobile, lang, code) => {
    try {
      const response = await axios.post('/auth/accept_invitation', { name, password, mobile, lang, code });
      const { token, user } = response.data;
      setSession(token, user?.business?.id);
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
      setChatSession(user);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await axios.post('/auth/profile', data);
      const { user } = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const setUserCounts = async (data) => {
    try {
      await axios.post('/auth/set_counts', data);
    } catch (error) {
      console.error(error);
    }
  };

  const egsOnboarding = async (data) => {
    try {
      await axios.post('egs/', data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        hasPermissions,
        login,
        logout,
        getSSOUrl,
        loginSSO,
        register,
        forgotPassword,
        verifyResetPasswordCode,
        resetPassword,
        changePassword,
        verifyActivationCode,
        sendActivationEmail,
        checkOTP,
        createDemo,
        checkDemo,
        deleteData,
        setBusiness,
        checkDiscount,
        placeSubscription,
        verifySubscription,
        updateBusiness,
        partialUpdateBusiness,
        setSystemParameter,
        sendInvitationEmail,
        getInvitation,
        acceptInvitation,
        updateProfile,
        setUserCounts,
        egsOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
